import { ChartInsight } from '@api';
import { ChartAPIReturn, EventsConfig } from '@chart';
import {
  POSTHOG_TRACKING_SOURCES,
  PosthogTrackingSource,
} from '@terminal/services/tracking/posthog/constants';
import { RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { useInsightsInDomain } from '../../hooks/use-insights-in-domain/useInsightsInDomain';
import { ChartInsightIcon } from './chart-insight-icon/ChartInsightIcon';
import styles from './Insights.module.scss';

export interface InsightsProps {
  chartApiRef: RefObject<ChartAPIReturn | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  insights: ChartInsight[];
  eventsConfig: EventsConfig;
  isSimpleChart?: boolean;
  chartWidth?: number;
  chartHeight?: number;
  minIconY?: number;
  minRightOffset?: number;
  source?: PosthogTrackingSource;
  disableClick?: boolean;
}

export const Insights = ({
  chartApiRef,
  canvasRef,
  insights,
  eventsConfig,
  isSimpleChart,
  chartWidth,
  chartHeight,
  minIconY,
  minRightOffset,
  source = POSTHOG_TRACKING_SOURCES.CHART,
  disableClick = false,
}: InsightsProps) => {
  const { t } = useTranslation(['chart']);

  const { insightsInDomain, zoomIntoViewText } = useInsightsInDomain({
    canvasRef,
    eventsConfig,
    chartApiRef,
    insights,
    chartWidth,
    chartHeight,
  });

  return (
    <>
      {insightsInDomain.map((domainEntry, insightsInDomainIndex) => (
        <ChartInsightIcon
          key={insightsInDomainIndex}
          insightIconRight={Math.max(domainEntry.right, minRightOffset ?? 0)}
          insightIconTop={domainEntry.top}
          yOffsetCount={domainEntry.yOffsetCount}
          minIconY={minIconY}
          groupedInsights={domainEntry.groupedInsights}
          isSimpleChart={isSimpleChart}
          source={source}
          disableClick={disableClick}
        />
      ))}

      {zoomIntoViewText && (
        <p
          className={styles.zoomIntoViewText}
          data-testid="zoom-in-required"
          style={{
            top: zoomIntoViewText.y,
            right: zoomIntoViewText.right,
          }}
        >
          {t('chart:zoomInToView')}
        </p>
      )}
    </>
  );
};
